/* eslint-disable react-hooks/exhaustive-deps */
import { ContactTypeId } from '@constants/enums';
import { PERSON } from '@constants/general';
import { Avatar } from '@efexteam/components';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Box, Chip, Typography } from '@mui/material';
import { selectProfileData } from '@pages/profile/store/selectors';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AutomCompleteBar from '../../components/searchBar/AutoCompleteBar';
import { COUNTRY_FLAG_ICON } from '../../configs/language/config';
import useDebounce from '../../hooks/useDebounce';
import clientSvc from '../../services/client/clientSvc';
import { IClientModal } from '../../services/client/types';
import { aesDecryptCBC } from '../../utils/crypt';

const getEntityName = (client?: IClientModal): string => {
  if (!client) return '';
  return client.entityBusiness === PERSON ? client.name : client.companyName;
};

interface SearchClientInputProps {
  className?: string;
  onSelectClient: (client: IClientModal | undefined) => void;
}

const SearchClientInput = ({
  className,
  onSelectClient,
}: SearchClientInputProps) => {
  const { t } = useTranslation();
  const userProfile = useSelector(selectProfileData);
  const [displayData, setDisplayData] = useState<IClientModal[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientModal>();
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState<string>('');
  const debouncedSearchCriteria = useDebounce<string>(searchCriteria);
  const CLIENTS_GROUPS = {
    users: t('optionsUsersLabel'),
    clients: t('optionsContactsLabel'),
  };

  const enterpriseId = useMemo(() => {
    if (userProfile?.company) return userProfile?.company?.enterpriseId;
    return null;
  }, [userProfile]);

  const getContactsEmails = (clientsModal: IClientModal[]) => {
    setDisplayData(clientsModal);
    setLoading(false);
  };

  const handleChange = (_: any, client: IClientModal) => {
    if (!client) return;
    const currentClient = displayData.find((user) => user.uuid === client.uuid);
    if (currentClient) {
      setSelectedClient(currentClient);
      onSelectClient(currentClient);
    }
  };

  const handleChangeCapture = async (event: any) => {
    const { value } = event.target;
    if (value.length >= 2) {
      setLoading(true);
      setSearchCriteria(value);
    } else {
      setSelectedClient(undefined);
      onSelectClient(undefined);
      setDisplayData([]);
    }
  };

  const searchData = async (data: string) => {
    if (searchCriteria.length) {
      const contacts = await clientSvc.searchContactByEmail(
        data,
        enterpriseId || ''
      );
      getContactsEmails(contacts.clientData);
    } else {
      setDisplayData([]);
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setSelectedClient(undefined);
    onSelectClient(undefined);
    setDisplayData([]);
  };

  useEffect(() => {
    searchData(searchCriteria);
  }, [debouncedSearchCriteria]);

  return (
    <>
      <AutomCompleteBar
        key={selectedClient?.name}
        loading={loading}
        data={displayData}
        className={clsx(className, 'mb-2')}
        onGetOptionLabel={(client) =>
          `${getEntityName(selectedClient)}-(${client.name})`
        }
        renderOption={(client) => (
          <Box className="flex space-x-2 items-center w-full">
            <Avatar username={getEntityName(client)} size="sm" />
            <Box>
              <Typography variant="body2" className="m-0">
                <Typography component="span" className="m-0 text-sm">
                  {getEntityName(client)}
                </Typography>
                {client.email && (
                  <Typography
                    component="span"
                    className="text-xs text-gray-400"
                  >
                    {' '}
                    ({aesDecryptCBC(client.email)})
                  </Typography>
                )}
              </Typography>
              {client?.nickname && (
                <p className="m-0 text-xs text-gray-400">{client.nickname}</p>
              )}
            </Box>
            {client.contactTypeId !== ContactTypeId.SWIFT_CONTACT ? (
              <Box className="!ml-auto">
                {
                  COUNTRY_FLAG_ICON[
                    (client.country as keyof typeof COUNTRY_FLAG_ICON) || 'USA'
                  ]
                }
              </Box>
            ) : null}
          </Box>
        )}
        inputParams={(params) => {
          const value = params.inputProps?.value as string;

          return {
            value: value || getEntityName(selectedClient),
            placeholder: t('textSearchEmail'),
          };
        }}
        icon={
          selectedClient ? (
            <Avatar username={getEntityName(selectedClient)} size="sm" />
          ) : (
            <AccountCircle
              sx={{ width: '30px', height: '30px', color: '#00143A' }}
            />
          )
        }
        groupBy={(option) => option.origin}
        groupsTitle={CLIENTS_GROUPS}
        onChange={handleChange}
        onChangeCapture={handleChangeCapture}
        onInputChange={handleChange}
        emptyState={
          <Box className="text-center">
            <SearchOffIcon sx={{ width: 50, height: 50 }} />
            <p className="text-sm m-0">{t('textSearchNotFound')}</p>
          </Box>
        }
      />
      {selectedClient?.name && (
        <Box className="w-full text-left">
          <Chip
            avatar={<ContactMailIcon />}
            label={selectedClient?.name}
            className="text-sm px-2"
            size="small"
            onDelete={handleDelete}
          />
        </Box>
      )}
    </>
  );
};

export default SearchClientInput;
